/**
 * Staff Management API Contract Schemas
 *
 * These Zod schemas define the API contracts for staff management endpoints.
 * They serve as the single source of truth for:
 * - Backend request validation
 * - Frontend/Mobile form validation
 * - TypeScript type definitions
 *
 * @module schemas/staff
 */

import { z } from 'zod';

// Staff Permission enum values
const StaffPermissionValues = [
  'VIEW_BOOKINGS',
  'MANAGE_BOOKINGS',
  'VIEW_REVENUE',
  'VIEW_PAYOUTS',
  'MANAGE_AVAILABILITY',
  'MANAGE_VENUE_DETAILS',
  'VIEW_REVIEWS',
  'RESPOND_TO_REVIEWS',
  'MANAGE_COUPONS',
  'VIEW_CUSTOMERS',
  'VIEW_STAFF',
  'MANAGE_STAFF',
  'VIEW_ANALYTICS',
] as const;

// ============================================================================
// INVITE STAFF
// ============================================================================

/**
 * Invite staff member request schema
 *
 * Validation rules:
 * - email: Must be a valid email format
 * - firstName: Minimum 2 characters
 * - lastName: Minimum 2 characters
 * - permissions: Array of valid StaffPermission values
 * - assignedVenues: Optional array of venue IDs
 */
export const InviteStaffRequestSchema = z.object({
  email: z.string().email('Invalid email format'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  permissions: z.array(z.enum(StaffPermissionValues)).min(1, 'At least one permission is required'),
  assignedVenues: z.array(z.string().uuid()).optional().default([]),
});

export type InviteStaffRequest = z.infer<typeof InviteStaffRequestSchema>;

/**
 * Invite staff response schema
 */
export const InviteStaffResponseSchema = z.object({
  message: z.string(),
  invitation: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    expiresAt: z.string(),
  }),
});

export type InviteStaffResponse = z.infer<typeof InviteStaffResponseSchema>;

// ============================================================================
// ACCEPT INVITATION
// ============================================================================

/**
 * Accept staff invitation request schema
 *
 * Validation rules:
 * - token: Required invitation token
 * - password: Same rules as registration password
 */
export const AcceptInvitationRequestSchema = z.object({
  token: z.string().min(1, 'Invitation token is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
});

export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequestSchema>;

/**
 * Accept invitation response schema
 */
export const AcceptInvitationResponseSchema = z.object({
  message: z.string(),
  access_token: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.literal('VENDOR_STAFF'),
  }),
});

export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponseSchema>;

// ============================================================================
// VERIFY INVITATION
// ============================================================================

/**
 * Verify invitation token request schema
 */
export const VerifyInvitationRequestSchema = z.object({
  token: z.string().min(1, 'Invitation token is required'),
});

export type VerifyInvitationRequest = z.infer<typeof VerifyInvitationRequestSchema>;

/**
 * Verify invitation response schema
 */
export const VerifyInvitationResponseSchema = z.object({
  valid: z.boolean(),
  invitation: z
    .object({
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      vendorName: z.string(),
      expiresAt: z.string(),
    })
    .optional(),
  error: z.string().optional(),
});

export type VerifyInvitationResponse = z.infer<typeof VerifyInvitationResponseSchema>;

// ============================================================================
// UPDATE STAFF
// ============================================================================

/**
 * Update staff member request schema
 *
 * Validation rules:
 * - permissions: Optional array of valid StaffPermission values
 * - assignedVenues: Optional array of venue IDs
 * - isActive: Optional boolean to activate/deactivate staff
 */
export const UpdateStaffRequestSchema = z.object({
  permissions: z.array(z.enum(StaffPermissionValues)).optional(),
  assignedVenues: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional(),
});

export type UpdateStaffRequest = z.infer<typeof UpdateStaffRequestSchema>;

/**
 * Update staff response schema
 */
export const UpdateStaffResponseSchema = z.object({
  message: z.string(),
  staff: z.object({
    id: z.string(),
    permissions: z.array(z.enum(StaffPermissionValues)),
    assignedVenues: z.array(z.string()),
    isActive: z.boolean(),
  }),
});

export type UpdateStaffResponse = z.infer<typeof UpdateStaffResponseSchema>;

// ============================================================================
// LIST STAFF
// ============================================================================

/**
 * Staff list query parameters schema
 */
export const StaffListQuerySchema = z.object({
  page: z.coerce.number().min(1).optional().default(1),
  limit: z.coerce.number().min(1).max(100).optional().default(20),
  search: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
  venueId: z.string().uuid().optional(),
});

export type StaffListQuery = z.infer<typeof StaffListQuerySchema>;

/**
 * Staff member response schema
 */
export const StaffMemberSchema = z.object({
  id: z.string(),
  vendorId: z.string(),
  userId: z.string(),
  permissions: z.array(z.enum(StaffPermissionValues)),
  assignedVenues: z.array(z.string()),
  isActive: z.boolean(),
  invitedBy: z.string(),
  invitedAt: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    avatar: z.string().nullish(),
  }),
  lastActive: z.string().nullish(),
});

export type StaffMemberResponse = z.infer<typeof StaffMemberSchema>;

/**
 * Staff list response schema
 */
export const StaffListResponseSchema = z.object({
  staff: z.array(StaffMemberSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

export type StaffListResponse = z.infer<typeof StaffListResponseSchema>;

// ============================================================================
// PENDING INVITATIONS
// ============================================================================

/**
 * Pending invitation schema
 */
export const PendingInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  permissions: z.array(z.enum(StaffPermissionValues)),
  assignedVenues: z.array(z.string()),
  expiresAt: z.string(),
  createdAt: z.string(),
});

export type PendingInvitation = z.infer<typeof PendingInvitationSchema>;

/**
 * Pending invitations list response schema
 */
export const PendingInvitationsResponseSchema = z.object({
  invitations: z.array(PendingInvitationSchema),
});

export type PendingInvitationsResponse = z.infer<typeof PendingInvitationsResponseSchema>;

// ============================================================================
// RESEND INVITATION
// ============================================================================

/**
 * Resend invitation response schema
 */
export const ResendInvitationResponseSchema = z.object({
  message: z.string(),
  expiresAt: z.string(),
});

export type ResendInvitationResponse = z.infer<typeof ResendInvitationResponseSchema>;

// ============================================================================
// DELETE STAFF / REVOKE INVITATION
// ============================================================================

/**
 * Delete staff response schema
 */
export const DeleteStaffResponseSchema = z.object({
  message: z.string(),
});

export type DeleteStaffResponse = z.infer<typeof DeleteStaffResponseSchema>;

/**
 * Revoke invitation response schema
 */
export const RevokeInvitationResponseSchema = z.object({
  message: z.string(),
});

export type RevokeInvitationResponse = z.infer<typeof RevokeInvitationResponseSchema>;
